import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import getApolloErrors from '../../../../../server/utils/getApolloErrors';
import { type AutoplayModuleSpecsFragment } from '../../../../api/fragments/AutoplayModuleSpecs';
import {
    type DeleteAutoplayModuleSettingMutation,
    type DeleteAutoplayModuleSettingMutationVariables,
    DeleteAutoplayModuleSettingDocument,
} from '../../../../api/mutations/deleteAutoplayModuleSetting';

export type AutoplaySettingActionsProps = {
    module: AutoplayModuleSpecsFragment;
    setting: AutoplayModuleSpecsFragment['settings'][number];
};

const AutoplaySettingActions = ({ module, setting }: AutoplaySettingActionsProps) => {
    const moduleId = module.id;
    const paymentSettingId = setting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['autoplayModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('autoplayModuleDetails:autoplaySettings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('autoplayModuleDetails:autoplaySettings.deleteModal.content'),
            okText: t('autoplayModuleDetails:autoplaySettings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('autoplayModuleDetails:autoplaySettings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('autoplayModuleDetails:autoplaySettings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                try {
                    await apolloClient
                        .mutate<DeleteAutoplayModuleSettingMutation, DeleteAutoplayModuleSettingMutationVariables>({
                            mutation: DeleteAutoplayModuleSettingDocument,
                            variables: { moduleId, settingId: paymentSettingId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('autoplayModuleDetails:autoplaySettings.deleteModal.messages.success'),
                        key: 'primary',
                    });
                } catch (error) {
                    message.destroy('primary');

                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors !== null) {
                        const { $root: rootError, ...fieldErrors } = apolloErrors;

                        if (rootError) {
                            message.error(rootError);
                        } else if (fieldErrors) {
                            message.error(Object.values(fieldErrors).join('\n'));
                        }
                    } else {
                        console.error(error);
                    }
                }
            },
        });
    }, [apolloClient, moduleId, paymentSettingId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/autoplay/${paymentSettingId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('autoplayModuleDetails:autoplaySettingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('autoplayModuleDetails:autoplaySettingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default AutoplaySettingActions;
